import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { CompaniesPage, InstitutionalsGateway } from '../infrastructure/institutionals.gateway';
import { institutionalsRequested, pageChanged, pageSizeChanged } from './institutionals.actions';
import { selectPage, selectPageSize } from './institutionals.selectors';
import { institutionalsActions } from './institutionals.slice';

const LOAD_ERROR = 'Failed to load companies.';

/** Best-effort human message for the failure banner (no domain error union). */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : LOAD_ERROR;
}

/**
 * WORKER: fetch the current page. Reads `page` + `pageSize` back from the store
 * (the single source of truth for the fetch) and calls the gateway. `takeLatest`
 * cancels an in-flight fetch when a newer trigger arrives; the abort-latest
 * behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  const gateway: InstitutionalsGateway = yield getContext('institutionalsGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);

  yield put(institutionalsActions.loadStarted());
  try {
    const result = (yield call(gateway.fetchCompanies, { page, pageSize })) as CompaniesPage;
    yield put(institutionalsActions.loadSucceeded(result));
  } catch (e) {
    yield put(institutionalsActions.loadFailed(toMessage(e)));
  }
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(institutionalsActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(institutionalsActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/** WATCHERS. */
export function* institutionalsSaga() {
  yield all([
    takeLatest(institutionalsRequested.type, fetchCurrentPage),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
  ]);
}
